import React, { useMemo, useState } from "react";
import { DiseaseCluster } from "fe-common/hooks/sortFeaturesWithCrs";
import { RecommendedPlanSection } from "../recommendedPlan/recommendedPlanSection";
import { CATEGORY } from "fe-common/analytics/analytics";
import { DiseaseCategoryType } from "fe-common/graphql";
import { PatientCaseChange, usePatientCase } from "../usePatientCase";
import { DDClinicalReasoning } from "fe-common/graphql/dao";
import { MobileDiagnoseButton } from "../common/rosebudButtomNavigationButton";
import { styled } from "@mui/material";
import { useScreenSize } from "../util/useScreenSize";
import { useHistory } from "react-router-dom";
import { AgentRoute } from "../util/agentRoutes";
import { MobileDiagnoseButtonContainerStyled } from "../rosebud/mobilePatientCase";
import { PlanSection } from "../recommendedPlan/planSection";
import { useSuggestions } from "../hooks/useSuggestions";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";
import { crToQualifiedFeature } from "fe-common/logic";

//region [[ Styles ]]
const MobileDiagnoseButtonWrapper = styled(MobileDiagnoseButtonContainerStyled)(
  {
    bottom: "0",
    left: "0",
    zIndex: 100,
  },
);

const MiniDiseasePlanSection = styled(PlanSection)({
  "& .in-cluster": {
    paddingLeft: "25px",
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]
export interface MiniPlanProps {
  name: string;
  category: DiseaseCategoryType;
  fWithCrs: DiseaseCluster;
  selectedCR: DDClinicalReasoning;
}
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

const MiniDiseasePlan = ({
  name,
  category,
  fWithCrs,
  selectedCR,
}: MiniPlanProps) => {
  const [changed, setChanged] = useState<boolean>(false);

  const { patientCase } = usePatientCase();

  const [planCategory, setPlanCategory] = useState(
    DiseaseCategoryType.OutcomeTest,
  );

  const onChangePlanCategory = (newCategory: DiseaseCategoryType) => {
    setPlanCategory(newCategory);
  };

  const { isMobile } = useScreenSize();
  const {
    doChangesToPatientCase,
    applyPendingFindings,
    doChangesToPending,
  } = usePatientCase();
  const history = useHistory();

  const diseases = useMemo(
    () => (selectedCR ? [crToQualifiedFeature(selectedCR)] : [fWithCrs]),
    [fWithCrs, selectedCR],
  );

  const miniPlan = useSuggestions({ patientCase, diseases, planNeeded: true });

  const addChange = (change: PatientCaseChange) => {
    isMobile ? doChangesToPatientCase([change]) : doChangesToPending([change]);
  };

  const goToDiagnose = () => {
    applyPendingFindings();
    history.push(`/agent/${AgentRoute.DD}`);
  };

  const DiagnoseButton = () =>
    isMobile ? (
      <MobileDiagnoseButtonWrapper>
        <MobileDiagnoseButton onClick={goToDiagnose} disabled={!changed}>
          <i className={"fas fa-stethoscope"} />
          Update
        </MobileDiagnoseButton>
      </MobileDiagnoseButtonWrapper>
    ) : null;

  return (
    <>
      {category === DiseaseCategoryType.OutcomeSigns ? (
        <>
          {miniPlan.planIsLoading && <ThreeDotsLoader />}
          <RecommendedPlanSection
            title={name}
            category={category}
            suggestions={miniPlan.findingSuggestions}
            isLoading={miniPlan.planIsLoading}
            eventCategory={CATEGORY.MINI_DISEASE}
            isEmbedded={true}
            addChange={(change) => {
              setChanged(true);
              addChange(change);
            }}
          />
        </>
      ) : (
        <MiniDiseasePlanSection
          title={name}
          category={planCategory}
          suggestions={miniPlan.findingSuggestions}
          clusteredSuggestions={miniPlan.clusteredSuggestions}
          isLoading={miniPlan.planIsLoading}
          eventCategory={CATEGORY.MINI_DISEASE}
          isEmbedded={true}
          onChangeCategory={onChangePlanCategory}
          onChange={() => {
            setChanged(true);
          }}
        />
      )}
      <DiagnoseButton />
    </>
  );
};

export default MiniDiseasePlan;
